import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/app/store/authStore';
import { can, type Permission } from '@/domain/permissions';
import { FullPageSpinner } from '@/components/ui/Spinner';

interface RoleGuardProps {
  children: ReactNode;
  /** The route renders if the user holds ANY of these. */
  permissions?: Permission[];
}

/**
 * Route-level access control.
 *
 * This hides a route; it does not secure it. The API enforces the same matrix
 * on every request, so a user who edits the URL gets a 403 rather than data.
 */
export function RoleGuard({ children, permissions }: RoleGuardProps): JSX.Element {
  const { user, isAuthenticated, isInitialising } = useAuthStore();
  const location = useLocation();

  if (isInitialising) return <FullPageSpinner />;

  if (!isAuthenticated || !user) {
    // Remember where they were headed so login can return them there.
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // A password the admin set cannot be used for real work until it is changed.
  if (user.mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  if (permissions && permissions.length > 0) {
    const allowed = permissions.some((permission) => can(user, permission));
    if (!allowed) return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

/** Inline permission check for individual controls. */
export function Can({
  permission,
  children,
  fallback = null,
}: {
  permission: Permission;
  children: ReactNode;
  fallback?: ReactNode;
}): JSX.Element {
  const user = useAuthStore((state) => state.user);
  return <>{can(user, permission) ? children : fallback}</>;
}
